import React, { FC, useCallback, useEffect, useReducer } from 'react';

import {
  Box,
  Center,
  HStack,
  ListItem,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { useFlags } from 'app/flagsmith';
import { featureFlags, isFeatureEnabled } from 'app/flagsmith/features';
import {
  CustomTooltip,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { CopyIcon, PackagesIcon } from 'components/Icons';
import { renderVulnerabilities } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/utils';
import { ResourceVulnerabilitiesFilters } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ResourceVulnerabilitiesFilters';
import { fixPlanSourceOptions } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/utils';
import {
  exploitableOptions,
  vulnerabilitySeverityOptions,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { actions } from 'containers/Visibility/Vulnerabilities/slice';

export const ResourceVulnerabilityFixPlanTable: FC = () => {
  const { resourceInsight, patchRecommendations } = useSelector(
    selectVulnerabilities,
  );
  const { data: insight } = resourceInsight;
  const { data } = patchRecommendations;
  const dispatch = useDispatch();
  const { new_vuln_dashboard: vulnerabilityV3Flag } = useFlags([
    featureFlags.vulnerabilityV3,
  ]);
  const useScanVaultVulnerabilityFindings = isFeatureEnabled(
    vulnerabilityV3Flag,
  );
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      severity: vulnerabilitySeverityOptions[0],
      hasPublicExploit: exploitableOptions[1],
      source: fixPlanSourceOptions[0],
      currentRecord: {},
      copied: false,
    },
  );
  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  useEffect(() => {
    if (isEmpty(insight)) return;

    dispatch(
      actions.getPatchRecommendations({
        q: {
          environmentId: insight?.environment_id,
          resourceUuid: insight?.uuid,
          nativeResource: insight?.native_name,
          accountId: insight?.account,
          exploitable: state.hasPublicExploit?.value,
          source: state.source?.value,
          useScanVaultVulnerabilityFindings,
        },
      }),
    );
  }, [
    dispatch,
    insight,
    state.hasPublicExploit,
    state.source,
    useScanVaultVulnerabilityFindings,
  ]);

  // Resets the "Command copied" label 2s after each copy.
  useEffect(() => {
    if (!state.copied) return;
    const timer = setTimeout(() => {
      updateState({ copied: false });
    }, 2000);

    // Cleanup to prevent memory leaks
    return () => clearTimeout(timer);
  }, [state.copied]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Package</Box>,
      accessor: 'cve_id',
      align: 'left',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.package_type}
          defaultIcon={<PackagesIcon />}
        >
          <StackedCell
            upper={row?.package_name}
            lower={row?.impacted_version}
          />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Patch Version',
      accessor: 'patch_version',
      align: 'left',
    },
    {
      header: 'Source',
      accessor: 'source',
      align: 'left',
    },
    {
      header: 'CVEs Fixed',
      accessor: 'cves_fixed_by_severity',
      align: 'left',
      render: ({ row }) =>
        renderVulnerabilities({ value: row?.cves_fixed_by_severity }),
    },
    {
      header: 'Fixed By',
      accessor: 'fix_command',
      align: 'left',
      render: ({ row }) => (
        <HStack w="full" justify="space-between">
          <Text noOfLines={1} flex={1}>
            {row?.fix_command ?? '-'}
          </Text>
          {!!row?.fix_command && (
            <Box
              boxSize={5}
              color="primary"
              cursor="pointer"
              onClick={e => {
                e.stopPropagation();
                updateState({ copied: true, currentRecord: row });
                navigator.clipboard.writeText(row?.fix_command);
              }}
            >
              <Center>
                <CustomTooltip
                  label={
                    state.copied &&
                    state.currentRecord?.package_name === row?.package_name
                      ? 'Command copied'
                      : 'Copy command'
                  }
                  width={24}
                  textAlign="center"
                >
                  <CopyIcon />
                </CustomTooltip>
              </Center>
            </Box>
          )}
        </HStack>
      ),
    },
  ];

  const renderFixPlanSteps = text => {
    const parts = text.split(/(\d+)/);

    return (
      <Text fontSize="md">
        {parts.map((part, i) =>
          /^\d+$/.test(part) ? (
            <Text
              display="inline-block"
              key={i}
              bg={colorOpacity(customTheme.colors.red['400'], 0.1)}
              color={customTheme.colors.red['400']}
              px={1.5}
              borderRadius={6}
            >
              {part}
            </Text>
          ) : (
            part
          ),
        )}
      </Text>
    );
  };

  return (
    <BorderedBox flex={1} h="full" minH="500px">
      {patchRecommendations.isLoading ? (
        <CustomSpinner />
      ) : (
        <Stack w="full" h="full" spacing={4}>
          <Stack spacing={5}>
            <Box fontWeight={600}>Summary</Box>
            <Stack>
              {!!data.image_id && (
                <Text>
                  The virtual machine uses Amazon Machine Image{' '}
                  <Text display="inline-block" color="primary">
                    {data.image_id}
                  </Text>
                </Text>
              )}
              {!isEmpty(data?.summary?.steps) && (
                <>
                  <Text>Steps:</Text>
                  <UnorderedList>
                    {map(data?.summary?.steps, o => (
                      <ListItem pl={4}>
                        {renderFixPlanSteps(o.message)}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </>
              )}
              <Box>{patchRecommendations.data?.summary?.disclaimer}</Box>
            </Stack>
          </Stack>
          <Box borderBottom="1px solid" borderColor="componentBorder" mb={1} />
          <ResourceVulnerabilitiesFilters
            severity={state.severity}
            updateValue={updateValue}
            hasPublicExploit={state.hasPublicExploit}
            tab="fix-plan"
            source={state.source}
          />
          <Box flex={1} w="full" h="full">
            <Table
              data={patchRecommendations.data?.packages_to_patch ?? []}
              columns={columns}
              isLoading={patchRecommendations.isLoading}
            />
          </Box>
        </Stack>
      )}
    </BorderedBox>
  );
};
